<template>
  <PaginaAdministrar
    module-name="Campos Sensíveis (LGPD)"
    :loading="loading"
    :erro="erro"
    @adicionar="abrirModalNovo"
    @atualizar="buscarDados"
  >
    <AdminTable
      :columns="colunas"
      :data="campos"
      :loading="loading"
    >
      <template #contextType="{ row }">
        <span :class="['badge', row.contextType === 'Tenant' ? 'badge-ghost-info' : 'badge-ghost-primary']">
          {{ row.contextType || 'Master' }}
        </span>
      </template>
      <template #tabelaNome="{ row }">
        <code>{{ row.tabelaNome }}</code>
      </template>
      <template #campoNome="{ row }">
        <code>{{ row.campoNome }}</code>
      </template>
      <template #padraoMascaramento="{ row }">
        <span :class="['badge', getBadgeClass(row.padraoMascaramento)]">
          {{ row.padraoMascaramento }}
        </span>
      </template>
      <template #ativo="{ row }">
        <span :class="['badge', row.ativo ? 'badge-ghost-success' : 'badge-ghost-secondary']">
          {{ row.ativo ? 'Sim' : 'Não' }}
        </span>
      </template>
      <template #acoes="{ row }">
        <button type="button" class="btn btn-sm btn-outline-primary me-1" @click="editarCampo(row)" title="Editar">
          <i class="bi bi-pencil"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="confirmarExclusao(row)" title="Excluir">
          <i class="bi bi-trash"></i>
        </button>
      </template>
    </AdminTable>

    <!-- Modal de Cadastro/Edição -->
    <ModalFormularioV2
      v-model="modalVisivel"
      :titulo="modalEditando ? 'Editar Campo Sensível' : 'Novo Campo Sensível'"
      :loading="salvando"
      @confirmar="salvar"
    >
      <div class="row g-3">
        <div class="col-12">
          <CampoSelect
            id="contextType"
            label="Contexto"
            v-model="form.contextType"
            :options="contextOptions"
            option-value="id"
            option-label="label"
            required
            placeholder="Selecione o contexto..."
            :disabled="modalEditando"
            dica="Master = tabelas do sistema; Tenant = tabelas do cliente."
            @update:model-value="aoSelecionarContexto"
          />
        </div>
        <div class="col-12">
          <CampoSelect
            id="tabelaNome"
            label="Tabela"
            v-model="form.tabelaNome"
            :options="tabelasDisponiveis"
            option-value="id"
            option-label="label"
            required
            placeholder="Selecione a tabela..."
            :disabled="carregandoTabelas"
            @update:model-value="aoSelecionarTabela"
          />
        </div>
        <div class="col-12">
          <CampoSelect
            id="campoNome"
            label="Campo"
            v-model="form.campoNome"
            :options="camposDisponiveis"
            option-value="id"
            option-label="label"
            required
            placeholder="Selecione o campo..."
            :disabled="carregandoCampos || !form.tabelaNome"
          />
        </div>
        <div class="col-12">
          <label class="form-label">Padrão de Mascaramento</label>
          <select class="form-select" v-model="form.padraoMascaramento">
            <option value="COMPLETO">COMPLETO — substitui tudo por [MASCARADO]</option>
            <option value="PARCIAL_CPF">PARCIAL_CPF — mostra 3 primeiros + 2 últimos dígitos</option>
            <option value="PARCIAL_CNPJ">PARCIAL_CNPJ — mostra 4 primeiros + 2 últimos dígitos</option>
            <option value="PARCIAL_EMAIL">PARCIAL_EMAIL — mostra apenas o domínio</option>
            <option value="PARCIAL">PARCIAL — mostra 3 primeiros + 3 últimos caracteres</option>
          </select>
        </div>
        <div class="col-12">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="campoAtivo" v-model="form.ativo" />
            <label class="form-check-label" for="campoAtivo">Ativo</label>
          </div>
        </div>
      </div>
    </ModalFormularioV2>

    <!-- Modal de Confirmação de Exclusão -->
    <ConfirmationModal
      ref="confirmacaoRef"
      title="Excluir Campo Sensível"
      :message="mensagemExclusao"
      confirm-button-text="Sim, Excluir"
      cancel-button-text="Cancelar"
      @confirmed="excluirConfirmado"
    />
  </PaginaAdministrar>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'

const emit = defineEmits(['show-notification'])

const loading = ref(false)
const erro = ref(null)
const campos = ref([])
const modalVisivel = ref(false)
const modalEditando = ref(false)
const salvando = ref(false)
const form = reactive({
  contextType: 'Master',
  tabelaNome: '',
  campoNome: '',
  padraoMascaramento: 'COMPLETO',
  ativo: true
})
const formId = ref(null)
const confirmacaoRef = ref(null)
const exclusaoPendenteId = ref(null)
const mensagemExclusao = ref('')

// Opções de contexto (Master = Sistema, Tenant = Cliente)
const contextOptions = [
  { id: 'Master', label: 'Master (Sistema)' },
  { id: 'Tenant', label: 'Tenant (Cliente)' }
]

// Estado para selects dinâmicos de Tabela/Campo
const tabelasDisponiveis = ref([])
const camposDisponiveis = ref([])
const carregandoTabelas = ref(false)
const carregandoCampos = ref(false)

const colunas = computed(() => [
  { field: 'contextType', label: 'Contexto', width: 110, sortable: true, slotName: 'contextType' },
  { field: 'tabelaNome', label: 'Tabela', sortable: true, slotName: 'tabelaNome' },
  { field: 'campoNome', label: 'Campo', sortable: true, slotName: 'campoNome' },
  { field: 'padraoMascaramento', label: 'Padrão', sortable: true, slotName: 'padraoMascaramento' },
  { field: 'ativo', label: 'Ativo', width: 80, sortable: true, slotName: 'ativo' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

const buscarDados = async () => {
  loading.value = true
  erro.value = null
  try {
    const response = await api.get('/ConfiguracoesCamposSensiveis')
    campos.value = response.data
  } catch (error) {
    if (error.response?.status === 403) {
      campos.value = []
    } else {
      erro.value = 'Falha ao carregar campos sensíveis.'
      emit('show-notification', { message: 'Erro ao carregar campos sensíveis.', type: 'error' })
    }
  } finally {
    loading.value = false
  }
}

// --- Selects dinâmicos de Tabela/Campo (mesma origem do DicionarioMetadadosListas) ---
const buscarTabelas = async () => {
  if (!form.contextType) return
  carregandoTabelas.value = true
  try {
    const res = await api.get(`/DicionarioMetadadosListas/GetTables/${form.contextType}`)
    tabelasDisponiveis.value = res.data.map(t => ({ id: t, label: t }))
  } catch (error) {
    console.error('Erro ao buscar tabelas:', error)
    tabelasDisponiveis.value = []
  } finally {
    carregandoTabelas.value = false
  }
}

const buscarCampos = async () => {
  if (!form.tabelaNome) {
    camposDisponiveis.value = []
    form.campoNome = ''
    return
  }
  carregandoCampos.value = true
  try {
    const res = await api.get(`/DicionarioMetadadosListas/GetColumns/${form.contextType}/${form.tabelaNome}`)
    camposDisponiveis.value = res.data.map(c => ({ id: c, label: c }))
  } catch (error) {
    console.error('Erro ao buscar campos:', error)
    camposDisponiveis.value = []
  } finally {
    carregandoCampos.value = false
  }
}

const aoSelecionarContexto = () => {
  form.tabelaNome = ''
  form.campoNome = ''
  camposDisponiveis.value = []
  buscarTabelas()
}

const aoSelecionarTabela = () => {
  form.campoNome = ''
  camposDisponiveis.value = []
  if (form.tabelaNome) {
    buscarCampos()
  }
}

const abrirModalNovo = () => {
  modalEditando.value = false
  formId.value = null
  form.contextType = 'Master'
  form.tabelaNome = ''
  form.campoNome = ''
  form.padraoMascaramento = 'COMPLETO'
  form.ativo = true
  camposDisponiveis.value = []
  modalVisivel.value = true
}

const editarCampo = async (campo) => {
  modalEditando.value = true
  formId.value = campo.idConfiguracaoCampoSensivel
  form.contextType = campo.contextType || 'Master'
  form.tabelaNome = campo.tabelaNome
  form.campoNome = campo.campoNome
  form.padraoMascaramento = campo.padraoMascaramento
  form.ativo = campo.ativo
  camposDisponiveis.value = []
  modalVisivel.value = true
  // Garante que as tabelas do contexto correto estejam carregadas
  await buscarTabelas()
  // Carrega os campos da tabela selecionada para o select funcionar na edição
  if (campo.tabelaNome) {
    await buscarCampos()
  }
}

const salvar = async () => {
  if (!form.contextType || !form.tabelaNome || !form.campoNome) {
    emit('show-notification', { message: 'Preencha Contexto, Tabela e Campo.', type: 'warning' })
    return
  }
  salvando.value = true
  try {
    const payload = { ...form }
    if (modalEditando.value) {
      await api.put(`/ConfiguracoesCamposSensiveis/${formId.value}`, payload)
    } else {
      await api.post('/ConfiguracoesCamposSensiveis', payload)
    }
    modalVisivel.value = false
    emit('show-notification', { message: 'Campo sensível salvo com sucesso!', type: 'success' })
    await buscarDados()
  } catch (error) {
    emit('show-notification', { message: error.response?.data?.message || 'Erro ao salvar.', type: 'danger' })
  } finally {
    salvando.value = false
  }
}

const confirmarExclusao = (campo) => {
  exclusaoPendenteId.value = campo.idConfiguracaoCampoSensivel
  mensagemExclusao.value = `Excluir mapeamento de <strong>${campo.tabelaNome}.${campo.campoNome}</strong>?`
  confirmacaoRef.value?.show()
}

const excluirConfirmado = async (confirmed) => {
  if (!confirmed || exclusaoPendenteId.value === null) return
  const id = exclusaoPendenteId.value
  exclusaoPendenteId.value = null
  try {
    await api.delete(`/ConfiguracoesCamposSensiveis/${id}`)
    emit('show-notification', { message: 'Campo sensível excluído com sucesso!', type: 'success' })
    await buscarDados()
  } catch (error) {
    emit('show-notification', { message: 'Erro ao excluir.', type: 'danger' })
  }
}

const getBadgeClass = (padrao) => {
  const map = {
    COMPLETO: 'badge-ghost-danger',
    PARCIAL_CPF: 'badge-ghost-warning',
    PARCIAL_CNPJ: 'badge-ghost-warning',
    PARCIAL_EMAIL: 'badge-ghost-info',
    PARCIAL: 'badge-ghost-primary'
  }
  return map[padrao] || 'badge-ghost-secondary'
}

defineExpose({ buscarDados })

onMounted(() => {
  buscarDados()
  buscarTabelas()
})
</script>
